'use client';

import React from 'react';
import { FileText, BarChart3, ShieldCheck, DollarSign, ClipboardList, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import type { ReportExport, ReportType } from '@/lib/services/staffingService';
import Icon from '@/components/ui/AppIcon';


const TYPE_META: Record<ReportType, { label: string; icon: React.ElementType; color: string }> = {
  billable_hours: { label: 'Billable Hours', icon: BarChart3, color: 'text-primary' },
  staffing_utilization: { label: 'Staffing Utilization', icon: FileText, color: 'text-info' },
  credential_compliance: { label: 'Credential Compliance', icon: ShieldCheck, color: 'text-success' },
  invoice_summary: { label: 'Invoice Summary', icon: DollarSign, color: 'text-warning' },
  audit_export: { label: 'Audit Export', icon: ClipboardList, color: 'text-muted-foreground' },
};

interface Props {
  history: ReportExport[];
  loading: boolean;
}

export default function ExportHistory({ history, loading }: Props) {
  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-5 pt-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-600 text-foreground">Export History</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Audit trail of all reports generated for stakeholder visibility</p>
        </div>
        <span className="text-xs text-muted-foreground">{history.length} exports logged</span>
      </div>

      <div className="card-elevated overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                {['Report', 'Type', 'Period', 'Rows', 'Generated By', 'Timestamp', 'Status'].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-600 text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-border/50">
                    {Array.from({ length: 7 }).map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-3 bg-muted rounded animate-pulse w-24" /></td>
                    ))}
                  </tr>
                ))
              ) : history.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <FileText size={32} className="opacity-30" />
                      <p className="text-sm">No exports yet — generate a report to see it here</p>
                    </div>
                  </td>
                </tr>
              ) : (
                history.map((entry) => {
                  const meta = TYPE_META[entry.reportType] || TYPE_META.audit_export;
                  const Icon = meta.icon;
                  return (
                    <tr key={entry.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Icon size={14} className={meta.color} />
                          <span className="font-500 text-foreground">{entry.title}</span>
                        </div>
                        {entry.fileName && (
                          <p className="text-[11px] text-muted-foreground mt-0.5 ml-5">{entry.fileName}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-500 ${meta.color}`}>{meta.label}</span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">
                        {entry.periodStart && entry.periodEnd
                          ? `${entry.periodStart} → ${entry.periodEnd}`
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-foreground font-500">{entry.rowCount.toLocaleString()}</td>
                      <td className="px-4 py-3 text-muted-foreground">{entry.generatedByName || '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">{formatDate(entry.createdAt)}</td>
                      <td className="px-4 py-3">
                        {entry.status === 'completed' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-600 bg-success/10 text-success">
                            <CheckCircle size={10} /> Completed
                          </span>
                        ) : entry.status === 'failed' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-600 bg-danger/10 text-danger">
                            <AlertCircle size={10} /> Failed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-600 bg-warning/10 text-warning">
                            <Loader2 size={10} className="animate-spin" /> Pending
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary cards */}
      {!loading && history.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(TYPE_META).map(([type, meta]) => {
            const count = history.filter((h) => h.reportType === type).length;
            const Icon = meta.icon;
            return (
              <div key={type} className="card-elevated p-4 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0`}>
                  <Icon size={16} className={meta.color} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{meta.label}</p>
                  <p className="text-lg font-700 text-foreground">{count}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
